/**
 * Параметры для функции оценки результата работы
 */
interface ScoreInfoParams {
  /** ID работы (null - работа не отправлялась) */
  workId: number | null;
  /** 
   * Статус работы:
   * - 0: работа не отправлялась
   * - 5: предварительная оценка (автоматическая, в данном случае работа на проверке педагогом)
   * - 10: финальная оценка (проверка педагогом)
   */
  workStatus: 0 | 5 | 10;
  /** Флаг возможности пересдачи (1 - разрешено, 0 - запрещено) */
  isOpportunityRedo: 0 | 1;
  /** Минимальный процент для успешного прохождения (0-100) */
  minForSuccess: number;
  /** Фактически набранный процент баллов (0-100) */
  solutionPercentage: number;
  /** Роль пользователя (по умолчанию 'children') */
  role?: string;
}

/**
 * Результат оценки работы
 */
interface ScoreInfoResult {
  /** Доступна ли пересдача работы */
  isOpportunityToRedo: boolean;
  /** 
   * Описание прогресса:
   * - "Предварительно набрано X% из Y необходимых" для workStatus = 5
   * - "Набрано X% из Y необходимых" для workStatus = 10
   */
  progressDescription: string;
}

/**
 * Анализирует результат работы и определяет возможность пересдачи
 * @param {ScoreInfoParams} params - Входные параметры
 * @returns {ScoreInfoResult} Результат анализа
 * 
 * @example
 * // Пример 1: Работа проверена, баллов достаточно
 * getScoreInfo({
 *   workId: 123,
 *   workStatus: 10,
 *   isOpportunityRedo: 1,
 *   minForSuccess: 60,
 *   solutionPercentage: 75
 * });
 * // Возвращает: { 
 * //   isOpportunityToRedo: false, 
 * //   progressDescription: "Набрано 75% из 60 необходимых" 
 * // }
 * @example
 * // Пример 2: Работа проверена, баллов не достаточно
 * getScoreInfo({
 *   workId: 123,
 *   workStatus: 10,
 *   isOpportunityRedo: 1,
 *   minForSuccess: 60,
 *   solutionPercentage: 45
 * });
 * // Возвращает: { 
 * //   isOpportunityToRedo: true, 
 * //   progressDescription: "Набрано 45% из 60 необходимых" 
 * // }
 */
export const getScoreInfo = ({
  workId,
  workStatus,
  isOpportunityRedo,
  minForSuccess,
  solutionPercentage,
  role = 'children'
}: ScoreInfoParams): ScoreInfoResult => {
  const progressDescription = `${workStatus === 5 ? 'Предварительно набрано ' : 'Набрано '}${solutionPercentage}% из ${minForSuccess} необходимых`;

  if (workId && workStatus != 0 && isOpportunityRedo == 1 && role === 'children' && minForSuccess && solutionPercentage) {
    return {
      isOpportunityToRedo: Number(minForSuccess) > Number(solutionPercentage),
      progressDescription
    };
  }

  return {
    isOpportunityToRedo: false,
    progressDescription
  };
};